import type { Rarity } from "@/components/brand/RarityBadge";
import rarityTiers from "./rarityTiers.json";

const RATED: Record<string, string> = rarityTiers;
const TIERS: readonly Rarity[] = ["common", "rare", "epic", "legendary"];
/** The keyword fallback, most prestigious first. A short code counts only as a whole word. */
const KEYWORD_TIERS: readonly (readonly [RegExp, Rarity])[] = [
  [/secret|hyper|rainbow|starlight|special (illustration|art)|legendary|enchanted/, "legendary"],
  [/master ball|\b(gold|sec|sar)\b/, "legendary"],
  [/double|ultra|illustration|epic|super\s*rare/, "epic"],
  [/uncommon|rare|holo|super/, "rare"],
];

/**
 * Maps a catalog rarity string onto the four-tier brand `Rarity` used for CardTile's art border
 * (#413).
 *
 * Inputs include "Double Rare", "Special Illustration Rare", "Uncommon" and Lorcana's "Legendary"
 * or "Enchanted". Every rarity the catalog held when `rarityTiers.json` was generated has a tier
 * there, rated once by a calibrated model against its game's other rarities (TRO-1087; regenerate
 * with `trovie.tools.rarity_tiers`). Keywords missed codes like "SR", "SCR" and Magic's "M". A
 * rarity added since falls back to a keyword heuristic checked most prestigious first. It knows
 * "SEC", "SAR" and "Starlight Rare", which no catalog row carried when the table was generated
 * (TRO-1109). A string that matches nothing reads as `common`, the quietest real tier, never a
 * wrong flashy one. Rarity arrives in English (the adapter prefers the translation, #595), so
 * English keywords are the right match surface.
 *
 * No rarity at all returns `undefined`, not `common`. A generic unit or a catalog miss has no tier,
 * and `CardTile` draws its neutral border for exactly that case; `common` is a tier the data
 * asserted and gets its own accent. Calling every uncatalogued unit "common" would be a claim
 * the data cannot back, like the fake precision DESIGN.md §6 forbids for money.
 *
 * The literal `"None"` counts as absent: it is the catalog's own spelling of "no rarity", carried
 * by the sealed pseudo-sets (CONTEXT.md, Catalog rarity). A sealed box is not a common card, so it gets the neutral border.
 */
export function catalogRarityToBrand(raw: string | null | undefined): Rarity | undefined {
  const r = raw?.trim().toLowerCase();
  if (!r || r === "none") return undefined;
  // The key drops everything but letters and digits, so a display spelling ("Rare Holo LV.X") and a
  // provider's ("rare holo lv.x") find the same row.
  const key = r.replace(/[^a-z0-9]+/g, "");
  const rated = TIERS.find((tier) => tier === RATED[key]);
  if (rated) return rated;
  return KEYWORD_TIERS.find(([keywords]) => keywords.test(r))?.[1] ?? "common";
}

/**
 * Returns whether a card is a chase printing, the one condition under which a surface turns on
 * `CardTile`'s opt-in foil shimmer (#760).
 *
 * The shimmer is the brand layer's only named motion exception (ADR-0046, DESIGN.md §7), so the
 * trigger must be rare or the exception becomes the default. Only the top two tiers qualify: `epic`
 * (Ultra, Double or Illustration Rare) and `legendary` (Secret, Rainbow, Gold or Enchanted), the
 * premium foil printings a dealer shows off. `rare` is excluded because the heuristic above folds
 * plain "Uncommon" and "Rare" into it, which is most of any binder.
 *
 * Grading is not part of the test: a slabbed common is still a common card, and shimmering every
 * graded unit would put motion on the routine case.
 *
 * Absent rarity is `false`, by the same unknown-is-not-a-tier rule as the border above.
 */
export function isChaseRarity(rarity: Rarity | undefined): boolean {
  return rarity === "epic" || rarity === "legendary";
}
